/**
 * Settings Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSettings hook.
 */

import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import {
  useSettings,
  ProfileTab,
  NotificationsTab,
  SecurityTab,
  APIAccessTab,
  OutOfOfficeTab,
} from './settings-page';

/** Tabs a link may open with `?tab=` (the out-of-office notices link here). */
const LINKABLE_TABS = new Set(['profile', 'notifications', 'out-of-office', 'security', 'api']);

// CR-5370: Company tab removed from My Account — company configuration
// lives in the admin/module areas. CR-5369: also removed the Login As
// section from the Security tab + the supporting imports/destructured
// values (isHelixAdmin / startImpersonation / useIsCustomer were only
// referenced from those removed surfaces).

export default function Settings() {
  const { user } = useAuth();
  // External-API token management is sensitive — it is restricted to
  // Staff, superusers, and customer admins. Supplier admins and supplier
  // users never see the API Access tab. Flags are typed on the User
  // interface in `lib/api/types/common.ts`.
  const isSupplierUser =
    user?.platform === 'SUPPLIER' || user?.company_type === 'supplier';
  // CR-6543 r4: API token management is superadmin/staff-only for now —
  // deliberately hidden from tenant company admins (backend enforces the
  // same rule, this just hides the tab).
  const canManageApiTokens =
    !isSupplierUser &&
    (user?.is_staff === true || user?.is_superuser === true);
  // CR-5889: the tab follows `?tab=`, so a notification link opens Out of
  // Office even when My Account is already on screen.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') ?? '';
  const tab = LINKABLE_TABS.has(requested) && (requested !== 'api' || canManageApiTokens)
    ? requested
    : 'profile';
  const onTabChange = (value: string) => setSearchParams({ tab: value }, { replace: true });
  const {
    // Profile
    profileForm,
    loadingProfile,
    savingProfile,
    handleProfileChange,
    handleSaveProfile,

    // Notifications
  } = useSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Account</h1>
        <p className="text-muted-foreground mt-2">
          Manage your personal preferences and account settings.
        </p>
      </div>

      {/* Settings Tabs */}
      <Tabs value={tab} onValueChange={onTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="out-of-office">Out of Office</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          {canManageApiTokens && <TabsTrigger value="api">API Access</TabsTrigger>}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <ProfileTab
            profileForm={profileForm}
            loadingProfile={loadingProfile}
            savingProfile={savingProfile}
            onProfileChange={handleProfileChange}
            onSaveProfile={handleSaveProfile}
          />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          {/* Loads and writes the subscription tree itself — the legacy
              preference row is no longer this screen's source of truth. */}
          <NotificationsTab />
        </TabsContent>

        {/* Out of Office Tab — CR-5889 */}
        <TabsContent value="out-of-office" className="space-y-4">
          <OutOfOfficeTab />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          <SecurityTab />
        </TabsContent>

        {/* API Access Tab — admin / superuser only */}
        {canManageApiTokens && (
          <TabsContent value="api" className="space-y-4">
            <APIAccessTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
